import { memo, useCallback, useEffect, useRef } from 'react';
import { mapAccentForRole } from '../data/portfolioNodes';
import { birthplaceLit, chainToRoot, drawLineage, drawLineageArcs, drawLineageGlow, drawLineageSpokes, layoutLineage, lineageDisplayPositions, lineageHitTest, lineagePhotoEpoch, lineageRingSlices, shiftLineage, stepLineageMotion, stepRingMotion } from './LineageCanvas';

function publicAsset(path) {
  if (!path) return '';
  if (path.startsWith('/') || path.startsWith('http')) return path;
  const base = process.env.PUBLIC_URL || '';
  const slash = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${slash}/${path}`;
}

/**
 * w, hSim: inner content box (excludes bottom padding) — orbit minDim and hub cy stay tied to this so radii do not change.
 * hDraw: height through bottom padding — canvas bitmap uses this so labels and nodes are not clipped on an invisible line above the footer band.
 */
function mapLayoutSize(el) {
  if (!el) return { w: 0, hSim: 0, hDraw: 0 };
  const cs = window.getComputedStyle(el);
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pr = parseFloat(cs.paddingRight) || 0;
  const pt = parseFloat(cs.paddingTop) || 0;
  const pb = parseFloat(cs.paddingBottom) || 0;
  const w = Math.max(0, el.clientWidth - pl - pr);
  const hSim = Math.max(0, el.clientHeight - pt - pb);
  const hDraw = Math.max(0, el.clientHeight - pt);
  return { w, hSim, hDraw };
}

const VIEWPORT_OPEN = 'width=device-width, initial-scale=1.0, minimum-scale=1, maximum-scale=1, user-scalable=no';

function setViewport(content) {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta || meta.getAttribute('content') === content) return;
  meta.setAttribute('content', content);
}

/** Map screen coords to simulation space — fixes hover when CSS layout size ≠ logical w×h (flex/subpixel). */
function clientToCanvasLocal(clientX, clientY, canvasEl, w, h) {
  const cr = canvasEl.getBoundingClientRect();
  if (cr.width <= 0 || cr.height <= 0) return { mx: 0, my: 0 };
  const mx = ((clientX - cr.left) / cr.width) * w;
  const my = ((clientY - cr.top) / cr.height) * h;
  return { mx, my };
}

/** Match header wordmark: KMM (Fabulist) + Orbitron fallback */
function titleFont(sizePx, weight = 600) {
  return `${weight} ${sizePx}px KMM, Orbitron, sans-serif`;
}

/** Canvas project labels — tighter than header wordmark */
const TITLE_TRACKING_EM = 0.12;
/** Scale image to cover the clip circle (may crop edges) — object-fit: cover */
function drawImageCoverInCircle(ctx, img, cx, cy, clipRadius) {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih || clipRadius <= 0) return;
  const d = clipRadius * 2;
  const scale = Math.max(d / iw, d / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, clipRadius, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
  ctx.restore();
}

/** Scale image so its entire bounding rect fits inside the clip circle (no corner crop). */
function drawImageContainedInCircle(ctx, img, cx, cy, clipRadius) {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih || clipRadius <= 0) return;
  const scale = (2 * clipRadius) / Math.hypot(iw, ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, clipRadius, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
  ctx.restore();
}

const BAND_F = [0.204, 0.286, 0.355, 0.424, 0.495];
const F_LO = 0.196;
const F_HI = 0.506;

/** Per-id wobble in normalized t-space so tiers aren’t perfectly uniform. */
function radiusTierWobble(id) {
  let h = 374761393;
  for (let i = 0; i < id.length; i++) {
    h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  }
  return (((h >>> 0) / 0xffffffff) - 0.5) * 0.036;
}

/**
 * Orbit radius from narrative band + connectivity. Outer-band “alone” nodes are pulled strongly
 * inward; moon parents get extra reach (band alone dominated before — e.g. craftfest vs cryodao).
 */
function mainNodeOrbitRadius(cfg, minDim, moonConfigs, mainLinks) {
  const band = cfg.orbitBand;
  const fRaw = BAND_F[Math.min(band, 4)] ?? 0.52;
  let t = (fRaw - F_LO) / (F_HI - F_LO);
  t = Math.max(0, Math.min(1, t));

  const hasMoons = moonConfigs.some((m) => m.parentId === cfg.id);
  const hasLink = mainLinks.some((l) => l.fromId === cfg.id || l.toId === cfg.id);
  const w = radiusTierWobble(cfg.id);

  let t2;
  if (hasMoons) t2 = Math.min(1, t + 0.44 + w);
  else if (hasLink) t2 = Math.min(1, t * 0.93 + 0.068 + w);
  else t2 = Math.max(0, t * 0.38 + w);

  t2 = Math.max(0, Math.min(1, t2));
  const fEff = F_LO + t2 * (F_HI - F_LO);
  const innerBoost = band === 1 ? 1.052 : 1;
  const viewportInset = 0.888;
  const moonReach = hasMoons ? 1.042 : 1;
  return minDim * fEff * innerBoost * viewportInset * MAIN_ORBIT_SCALE * moonReach;
}

const GOLDEN_ANGLE = 2.39996322972865332;

/** Default layout: pull rings inward (hub lines, springs, and radii stay consistent). */
const MAIN_ORBIT_SCALE = 0.9;
const MOON_ORBIT_SCALE = 0.92;

/** Phone portrait stays larger than the fixed-size project bubbles. */
function portraitRadius(minDim, w) {
  const base = minDim * 0.072 * MAIN_ORBIT_SCALE;
  if (w >= 768) return base;
  return Math.max(base, Math.min(w * 0.16, 68));
}

function clearOfPortrait(x, y, cx, cy, minDist) {
  const dx = x - cx;
  const dy = y - cy;
  const dist = Math.hypot(dx, dy);
  if (dist >= minDist) return { x, y };
  if (dist < 0.5) return { x: cx + minDist, y };
  const k = minDist / dist;
  return { x: cx + dx * k, y: cy + dy * k };
}

/**
 * Starting phase for angle slot 0 within a band (after alphabetical sort by node id).
 * Tuned so mid/outer rings open toward different screen quadrants instead of stacking on radials.
 * Stagger uses golden-angle mix — no per-company ids.
 */
function bandSectorBase(band) {
  const bases = [
    0.42,
    1.05,
    (7 * Math.PI) / 6,
    -Math.PI / 2,
    0.48 * Math.PI,
  ];
  const b = Math.min(Math.max(band, 0), 4);
  return (
    bases[b] +
    GOLDEN_ANGLE * band * 0.38 +
    Math.sin(band * 1.31 + 0.22) * 0.62 +
    Math.cos(band * 0.73) * 0.28
  );
}

/** Stable ± jitter so nodes on different rings rarely share a radial (no per-frame randomness). */
function stableOrbitJitter(id) {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (((h >>> 0) / 0xffffffff) - 0.5) * 0.48;
}

function distributeAngles(nodesConfig) {
  const countByBand = {};
  nodesConfig.forEach((n) => {
    countByBand[n.orbitBand] = (countByBand[n.orbitBand] || 0) + 1;
  });
  const byBand = {};
  nodesConfig.forEach((cfg) => {
    const band = cfg.orbitBand;
    (byBand[band] = byBand[band] || []).push(cfg);
  });
  Object.keys(byBand).forEach((k) => {
    byBand[k].sort((a, b) => a.id.localeCompare(b.id));
  });
  return nodesConfig.map((cfg) => {
    const band = cfg.orbitBand;
    const total = countByBand[band];
    const list = byBand[band];
    const sortedIdx = list.indexOf(cfg) + 1;
    const slot = sortedIdx - 1;
    const eq = (slot / total) * Math.PI * 2;
    const slotSkew = (GOLDEN_ANGLE * (slot + 0.5)) / (total + 1.618) / (2 * Math.PI) * 0.55;
    const slice = eq + slotSkew;
    const angle = slice + bandSectorBase(band) + stableOrbitJitter(cfg.id);
    return { cfg, angle, orbitR: 0 };
  });
}

function makeRuntime(nodesConfig, cx, cy, minDim, moonConfigs, mainLinks) {
  const placed = distributeAngles(nodesConfig);
  return placed.map(({ cfg, angle }) => {
    const orbitR = mainNodeOrbitRadius(cfg, minDim, moonConfigs, mainLinks);
    const x = cx + Math.cos(angle) * orbitR;
    const y = cy + Math.sin(angle) * orbitR;
    return {
      ...cfg,
      x,
      y,
      vx: 0,
      vy: 0,
      angle,
      orbitR,
      phase: Math.random() * Math.PI * 2,
      angularVel: 0.00012 + Math.random() * 0.0001,
    };
  });
}

function moonBodyRadius(cfg, minDim) {
  const hasIcon = Boolean(cfg.icon);
  const isMobile =
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)').matches;
  const mobileScale = isMobile ? 1.45 : 1;
  const minRadius = isMobile ? (hasIcon ? 13 : 11) : (hasIcon ? 9 : 7);
  return Math.max(minRadius, minDim * (hasIcon ? 0.023 : 0.017) * mobileScale);
}

/** Minimum orbit radius from parent so adjacent moons (plus labels) do not crowd. */
function moonChordMinOrbit(parent, nOnRing, maxMoonR, minDim) {
  const labelPad = minDim * 0.044;
  const minChord = maxMoonR * 2 + labelPad;
  const n = Math.max(nOnRing, 1);
  const sinHalf = Math.sin(Math.PI / n);
  if (sinHalf < 1e-4) return parent.bubbleRadius + minDim * 0.12;
  const fromChord = minChord / (2 * sinHalf);
  const floorR = parent.bubbleRadius + minDim * (0.09 + 0.0055 * Math.max(0, n - 2));
  return Math.max(floorR, fromChord);
}

function makeMoonRuntime(moonConfigs, mains, minDim) {
  const byParent = {};
  moonConfigs.forEach((c) => {
    (byParent[c.parentId] = byParent[c.parentId] || []).push(c);
  });
  const out = [];
  for (const parentId of Object.keys(byParent)) {
    const parent = mains.find((n) => n.id === parentId);
    const list = byParent[parentId];
    if (!parent) continue;
    const nMoons = list.length;
    const maxMoonR = Math.max(...list.map((c) => moonBodyRadius(c, minDim)));
    const maxPerRing = nMoons >= 4 ? Math.ceil(nMoons / 2) : nMoons;
    const nRing0 = Math.min(maxPerRing, nMoons);

    list.forEach((cfg, j) => {
      const ring = Math.floor(j / maxPerRing);
      const idxInRing = j % maxPerRing;
      const nThisRing = Math.min(maxPerRing, nMoons - ring * maxPerRing);
      const base =
        (idxInRing / nThisRing) * Math.PI * 2 +
        0.55 +
        ring * (Math.PI / Math.max(nRing0, 1));
      const ringBase = moonChordMinOrbit(parent, nThisRing, maxMoonR, minDim);
      const ringStep =
        minDim * (0.048 + 0.009 * Math.max(0, nMoons - 3) + 0.004 * Math.max(0, ring));
      const orbitR = (ringBase + ring * ringStep) * MOON_ORBIT_SCALE;
      const radius = moonBodyRadius(cfg, minDim);
      const x = parent.x + Math.cos(base) * orbitR;
      const y = parent.y + Math.sin(base) * orbitR;
      out.push({
        ...cfg,
        x,
        y,
        vx: 0,
        vy: 0,
        angle: base,
        orbitR,
        radius,
        phase: Math.random() * Math.PI * 2,
        angularVel: 0.00011 + Math.random() * 0.00007,
      });
    });
  }
  return out;
}

/** Extra space beyond bubble radius so stacked titles do not overlap neighbors (matches canvas title metrics). */
function mainTitleHalo(n, minDim) {
  const tfs = Math.max(6, minDim * 0.0125);
  const estLines = Math.min(3, Math.max(1, Math.ceil((n.title?.length || 0) / 20)));
  const stackBelow = 6 + estLines * (tfs + 2);
  const longTitleBoost = Math.min(minDim * 0.06, ((n.title?.length || 0) / 22) * (minDim * 0.012));
  return stackBelow * 0.72 + minDim * 0.032 + longTitleBoost;
}

function clampNodeSpeed(n, maxSpeed) {
  const v = Math.hypot(n.vx, n.vy);
  if (v <= maxSpeed || v < 1e-8) return;
  const s = maxSpeed / v;
  n.vx *= s;
  n.vy *= s;
}

/**
 * Tiny position-only nudges (no velocity) so moons don’t sit on top of each other.
 * Capped per frame — works with exponential moonFollow without runaway oscillation.
 */
function nudgeMoonSiblingsApart(moons, minDim, skipId) {
  const pad = minDim * 0.012;
  const labelGap = minDim * 0.015;
  const maxStep = minDim * 0.0042;
  const gain = 0.38;
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < moons.length; i++) {
      if (skipId && moons[i].id === skipId) continue;
      for (let j = i + 1; j < moons.length; j++) {
        if (skipId && moons[j].id === skipId) continue;
        const a = moons[i];
        const b = moons[j];
        if (a.parentId !== b.parentId) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy);
        const minDist = a.radius + b.radius + pad + labelGap;
        if (d >= minDist || d < 1e-4) continue;
        const nx = dx / d;
        const ny = dy / d;
        const step = Math.min((minDist - d) * gain, maxStep);
        a.x -= nx * step * 0.5;
        a.y -= ny * step * 0.5;
        b.x += nx * step * 0.5;
        b.y += ny * step * 0.5;
      }
    }
  }
}

/** Bubble-only clearance from other mains (no title halo — avoids orbit zippering). */
function nudgeMoonsFromOtherMainBubbles(moons, mains, minDim, skipMoonId) {
  const pad = minDim * 0.011;
  const maxStep = minDim * 0.0038;
  const gain = 0.26;
  for (const m of moons) {
    if (m.id === skipMoonId) continue;
    for (const n of mains) {
      if (n.id === m.parentId) continue;
      const dx = m.x - n.x;
      const dy = m.y - n.y;
      const d = Math.hypot(dx, dy);
      const minDist = n.bubbleRadius + m.radius + pad;
      if (d >= minDist || d < 1e-4) continue;
      const nx = dx / d;
      const ny = dy / d;
      const step = Math.min((minDist - d) * gain, maxStep);
      m.x += nx * step;
      m.y += ny * step;
    }
  }
}

function separateMainNodes(nodes, minDim, skipId) {
  const pad = minDim * 0.02;
  const strength = 0.44;
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < nodes.length; i++) {
      if (skipId && nodes[i].id === skipId) continue;
      for (let j = i + 1; j < nodes.length; j++) {
        if (skipId && nodes[j].id === skipId) continue;
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy);
        const minDist =
          a.bubbleRadius +
          b.bubbleRadius +
          pad +
          mainTitleHalo(a, minDim) * 0.92 +
          mainTitleHalo(b, minDim) * 0.92;
        if (d >= minDist || d < 1e-4) continue;
        const nx = dx / d;
        const ny = dy / d;
        const push = (minDist - d) * strength;
        a.x -= nx * push * 0.5;
        a.y -= ny * push * 0.5;
        b.x += nx * push * 0.5;
        b.y += ny * push * 0.5;
      }
    }
  }
}

function mergeMoonRuntime(prev, next) {
  const map = new Map(prev.map((m) => [m.id, m]));
  for (const n of next) {
    const o = map.get(n.id);
    if (o) {
      n.x = o.x;
      n.y = o.y;
      n.vx = o.vx * 0.5;
      n.vy = o.vy * 0.5;
      n.angle = o.angle;
      n.orbitR = o.orbitR;
    }
  }
  return next;
}

/** Extra hit slop for touch / coarse pointers (finger pads). */
function pointerHitSlop(pointerType) {
  if (pointerType === 'touch') return 16;
  if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches) return 10;
  return 0;
}

/** Keep a pan inside the content, or center it when the content fits. */
function clampPan(pan, minEdge, maxEdge, view, inset) {
  const lo = inset - maxEdge;
  const hi = view - inset - minEdge;
  if (lo > hi) return (view - (minEdge + maxEdge)) / 2;
  return Math.min(hi, Math.max(lo, pan));
}

/** Top-most hit by fake depth (higher y drawn later). */
function pickMapTarget(mx, my, mains, moons, slop = 0) {
  const hits = [];
  const mainPad = 14 + slop;
  const moonPad = 10 + slop;
  for (const n of mains) {
    const d = Math.hypot(mx - n.x, my - n.y);
    if (d < n.bubbleRadius + mainPad) hits.push({ kind: 'main', id: n.id, y: n.y, d });
  }
  for (const m of moons) {
    const d = Math.hypot(mx - m.x, my - m.y);
    if (d < m.radius + moonPad) hits.push({ kind: 'moon', id: m.id, y: m.y, d });
  }
  if (!hits.length) return null;
  hits.sort((a, b) => b.y - a.y || a.d - b.d);
  return hits[0];
}

function labelLines(text, maxPerLine = 18, maxLines = 3) {
  const t = (text || '').trim();
  if (!t) return [];
  if (t.length <= maxPerLine) return [t];
  const words = t.split(/\s+/).filter(Boolean);
  const lines = [];
  let i = 0;
  while (i < words.length && lines.length < maxLines) {
    let line = words[i];
    i++;
    while (i < words.length && `${line} ${words[i]}`.length <= maxPerLine) {
      line = `${line} ${words[i]}`;
      i++;
    }
    lines.push(line);
  }
  if (i < words.length && lines.length > 0) {
    const last = lines[lines.length - 1];
    const ell = '…';
    lines[lines.length - 1] =
      last.length + ell.length > maxPerLine ? `${last.slice(0, Math.max(0, maxPerLine - ell.length))}${ell}` : `${last}${ell}`;
  }
  return lines;
}

function PowerMapCanvas({
  nodes,
  moonNodes,
  mainLinks,
  selectionRef,
  placeHighlightRef,
  onSelectNode,
  onToggleLineage,
  onCloseLineage,
  lineageOpen,
  centerConfig,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const backRef = useRef(null);
  const runtimeRef = useRef([]);
  const moonRuntimeRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, in: false });
  const hoverRef = useRef(null);
  /** @type {React.MutableRefObject<{ mode: string; kind?: string; id?: string; offsetX?: number; offsetY?: number; startMx?: number; startMy?: number; pointerId?: number; dragThresh?: number }>} */
  const interactionRef = useRef({ mode: 'idle' });
  const lineageOpenRef = useRef(lineageOpen);
  const lineageProgressRef = useRef(0);
  const lineageLayoutRef = useRef(null);
  const lineageDisplayRef = useRef(null);
  const lineageMotionRef = useRef(new Map());
  const lineageRingsRef = useRef(null);
  const lineageHoverRef = useRef(null);
  const hubDrawRef = useRef(null);
  const onToggleLineageRef = useRef(onToggleLineage);
  const onCloseLineageRef = useRef(onCloseLineage);
  lineageOpenRef.current = lineageOpen;
  onToggleLineageRef.current = onToggleLineage;
  onCloseLineageRef.current = onCloseLineage;
  const imagesRef = useRef({});
  const sizeRef = useRef({ w: 0, h: 0, cx: 0, cy: 0, minDim: 0, xScale: 1, yScale: 1, bounds: null });
  const rafRef = useRef(0);
  const centerRef = useRef(centerConfig);
  const linksRef = useRef(mainLinks);
  centerRef.current = centerConfig;
  linksRef.current = mainLinks;

  const syncRuntimeSize = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const { w, hSim, hDraw } = mapLayoutSize(el);
    const minDim = Math.min(w, hSim);
    const cx = w / 2;
    const phone = w < 768;
    const layoutMinDim = phone ? 920 : minDim;
    const cy = hSim * 0.5;
    const hubR = portraitRadius(layoutMinDim, phone ? 800 : w);
    sizeRef.current = { w, h: hDraw, cx, cy, minDim: layoutMinDim, xScale: 1, yScale: 1, bounds: null, hubR };
    const prev = runtimeRef.current;
    const next = makeRuntime(nodes, cx, cy, layoutMinDim, moonNodes, mainLinks);
    if (!phone && prev.length === next.length) {
      for (let i = 0; i < next.length; i++) {
        const p = prev[i];
        const n = next[i];
        if (p && p.id === n.id) {
          n.x = p.x;
          n.y = p.y;
          n.vx = p.vx * 0.5;
          n.vy = p.vy * 0.5;
          n.angle = p.angle;
          n.orbitR = p.orbitR;
        }
      }
    }
    runtimeRef.current = next;

    const moonPrev = moonRuntimeRef.current;
    let moonNext = makeMoonRuntime(moonNodes, next, layoutMinDim);
    if (!phone && moonPrev.length === moonNext.length) moonNext = mergeMoonRuntime(moonPrev, moonNext);
    moonRuntimeRef.current = moonNext;
  }, [nodes, moonNodes, mainLinks]);

  useEffect(() => {
    const bucket = imagesRef.current;
    const load = (src) => {
      if (!src || bucket[src]) return;
      const im = new Image();
      im.src = publicAsset(src);
      bucket[src] = im;
    };
    load(centerConfig.icon);
    nodes.forEach((n) => load(n.icon));
    moonNodes.forEach((m) => load(m.icon));
  }, [nodes, moonNodes, centerConfig.icon]);

  useEffect(() => {
    let disposed = false;
    let stableW = 0;
    let lastOuter = window.outerWidth;
    let glitchUntil = 0;
    let viewportLocked = false;
    let pendingWide = 0;

    const stopMotion = () => {
      interactionRef.current = { mode: 'idle' };
      runtimeRef.current.forEach((n) => {
        n.vx = 0;
        n.vy = 0;
      });
      moonRuntimeRef.current.forEach((n) => {
        n.vx = 0;
        n.vy = 0;
      });
      mouseRef.current.in = false;
      mouseRef.current.overMap = false;
    };

    const measuredWidth = () => {
      const el = wrapRef.current;
      if (!el) return 0;
      return mapLayoutSize(el).w;
    };

    const holdNarrow = () => {
      if (!(stableW > 0 && stableW < 768)) return;
      window.clearTimeout(pendingWide);
      viewportLocked = true;
      setViewport(`width=${Math.round(stableW)}, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no`);
      document.documentElement.classList.add('is-narrow');
      stopMotion();
    };

    const commit = () => {
      if (disposed || document.hidden) return;
      const el = wrapRef.current;
      if (!el) return;
      const { w, hSim } = mapLayoutSize(el);
      if (w < 2 || hSim < 2) return;
      syncRuntimeSize();
      stableW = w;
      document.documentElement.classList.toggle('is-narrow', w < 768);
    };

    const glitching = () => document.hidden || !document.hasFocus() || performance.now() < glitchUntil;

    const schedule = () => {
      if (disposed || document.hidden) return;
      const w = measuredWidth();
      if (w < 2) return;

      const outer = window.outerWidth;
      if (outer > 0 && lastOuter > 0 && Math.abs(outer - lastOuter) > 50) {
        lastOuter = outer;
        viewportLocked = false;
        glitchUntil = 0;
        window.clearTimeout(pendingWide);
        setViewport(VIEWPORT_OPEN);
        commit();
        return;
      }
      if (outer > 0) lastOuter = outer;

      const jumped = stableW > 0 && stableW < 768 && w > stableW + 48;
      if (viewportLocked) {
        if (!glitching() && w < 768 && Math.abs(w - stableW) > 30) {
          viewportLocked = false;
          setViewport(VIEWPORT_OPEN);
          commit();
        }
        return;
      }
      if (jumped) {
        if (glitching()) {
          holdNarrow();
          return;
        }
        window.clearTimeout(pendingWide);
        pendingWide = window.setTimeout(() => {
          if (disposed) return;
          if (glitching()) {
            holdNarrow();
            return;
          }
          commit();
        }, 320);
        return;
      }
      window.clearTimeout(pendingWide);
      commit();
    };

    const markGlitch = () => {
      glitchUntil = performance.now() + 1600;
      window.clearTimeout(pendingWide);
      stopMotion();
      const w = measuredWidth();
      if (stableW > 0 && stableW < 768 && w > stableW + 48) holdNarrow();
    };

    const onVisibility = () => {
      markGlitch();
      if (!document.hidden) requestAnimationFrame(schedule);
    };
    const onBlur = () => markGlitch();
    const onFocus = () => {
      markGlitch();
      requestAnimationFrame(schedule);
    };

    schedule();
    const ro = new ResizeObserver(() => schedule());
    if (wrapRef.current) ro.observe(wrapRef.current);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      disposed = true;
      window.clearTimeout(pendingWide);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [syncRuntimeSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const localFromClient = (clientX, clientY) => {
      const { w, h } = sizeRef.current;
      return clientToCanvasLocal(clientX, clientY, canvas, w, h);
    };

    const hubHit = (mx, my, slop = 0) => {
      const drawn = hubDrawRef.current;
      if (drawn) return Math.hypot(mx - drawn.x, my - drawn.y) < drawn.r + 8 + slop;
      const { cx, cy, minDim, w, h } = sizeRef.current;
      const parallaxX = (mx - w / 2) * 0.028;
      const parallaxY = (my - h / 2) * 0.028;
      const hubR = sizeRef.current.hubR || portraitRadius(minDim, w);
      return Math.hypot(mx - (cx + parallaxX), my - (cy + parallaxY)) < hubR + 8 + slop;
    };

    /** Orbit center leans with the pointer, same offset as the ancestor fan. */
    const orbitCenterNow = () => {
      const { w, h, cx, cy, minDim } = sizeRef.current;
      const parallaxX = mouseRef.current.in ? (mouseRef.current.x - w / 2) * 0.028 : 0;
      const parallaxY = mouseRef.current.in ? (mouseRef.current.y - h / 2) * 0.028 : 0;
      return { tcx: cx + parallaxX, tcy: cy + parallaxY, minDim };
    };

    /** After drag, align the spring target to where the node was released. */
    const commitOrbitAfterDrag = (kind, id) => {
      const { tcx, tcy, minDim } = orbitCenterNow();
      if (kind === 'main') {
        const n = runtimeRef.current.find((x) => x.id === id);
        if (!n) return;
        const dx = n.x - tcx;
        const dy = n.y - tcy;
        let orb = Math.hypot(dx, dy);
        const minOrbit = minDim * 0.085;
        if (orb < minOrbit) orb = minOrbit;
        n.orbitR = orb;
        n.angle = Math.atan2(dy, dx);
        n.vx = 0;
        n.vy = 0;
        return;
      }
      if (kind === 'moon') {
        const m = moonRuntimeRef.current.find((x) => x.id === id);
        const p = runtimeRef.current.find((x) => x.id === m?.parentId);
        if (!m || !p) return;
        const dx = m.x - p.x;
        const dy = m.y - p.y;
        let orb = Math.hypot(dx, dy);
        const minOrbit = Math.max(10, minDim * 0.042);
        if (orb < minOrbit) orb = minOrbit;
        m.orbitR = orb;
        m.angle = Math.atan2(dy, dx);
        m.vx = 0;
        m.vy = 0;
      }
    };

    const dragThresholdFor = (pointerType) => (pointerType === 'touch' ? 12 : 5);
    const viewPan = { x: 0, y: 0, mode: null, w: 0, h: 0 };

    const worldFromScreen = (mx, my) => ({ x: mx - viewPan.x, y: my - viewPan.y });

    const panLimits = () => {
      const { w, h, cx, cy, hubR } = sizeRef.current;
      if (lineageProgressRef.current > 0.35 && lineageLayoutRef.current) {
        const layout = lineageLayoutRef.current;
        const half = layout.maxR * Math.sin((layout.arcSweep || Math.PI * 0.84) / 2);
        return {
          minX: layout.cx - half - 28,
          maxX: layout.cx + half + 28,
          minY: layout.cy - layout.maxR * (layout.yScale || 1) - 64,
          maxY: layout.cy + 120,
        };
      }
      let minX = cx - (hubR || 40);
      let maxX = cx + (hubR || 40);
      let minY = cy - (hubR || 40);
      let maxY = cy + (hubR || 40);
      const padNode = (x, y, r) => {
        minX = Math.min(minX, x - r - 48);
        maxX = Math.max(maxX, x + r + 48);
        minY = Math.min(minY, y - r - 24);
        maxY = Math.max(maxY, y + r + 56);
      };
      runtimeRef.current.forEach((n) => padNode(n.x, n.y, n.bubbleRadius || 20));
      moonRuntimeRef.current.forEach((m) => padNode(m.x, m.y, m.radius || 12));
      return { minX, maxX, minY, maxY, w, h };
    };

    const setPan = (x, y) => {
      const limits = panLimits();
      const { w, h } = sizeRef.current;
      viewPan.x = clampPan(x, limits.minX, limits.maxX, w, 64);
      viewPan.y = clampPan(y, limits.minY, limits.maxY, h, 64);
    };

    const activatePress = (hit) => {
      if (!hit) {
        if (selectionRef.current) onSelectNode(null);
        return;
      }
      if (hit.type === 'hub') {
        if (lineageOpenRef.current) onSelectNode(centerRef.current.id);
        else onToggleLineageRef.current?.();
        return;
      }
      if (hit.type === 'ancestor' || hit.type === 'node') {
        onSelectNode(selectionRef.current === hit.id ? null : hit.id);
      }
    };

    const identifyPress = (mx, my, slop) => {
      const { x, y } = worldFromScreen(mx, my);
      if (hubHit(x, y, slop)) return { type: 'hub' };
      if (lineageProgressRef.current > 0.45 && lineageDisplayRef.current) {
        const ancestorId = lineageHitTest(x, y, lineageDisplayRef.current, slop + 4);
        if (ancestorId && ancestorId !== 'I1') return { type: 'ancestor', id: ancestorId };
        if (lineageProgressRef.current > 0.72) return null;
      }
      const hit = pickMapTarget(x, y, runtimeRef.current, moonRuntimeRef.current, slop);
      if (hit) return { type: 'node', id: hit.id };
      return null;
    };

    const releaseCaptured = (pointerId) => {
      if (pointerId == null) return;
      try {
        if (wrap.hasPointerCapture(pointerId)) wrap.releasePointerCapture(pointerId);
      } catch (_) {
        /* capture may already be released */
      }
    };

    const finishPointerSession = (e) => {
      if (!e.isPrimary) return;
      const ir = interactionRef.current;
      if (ir.mode === 'idle') return;
      if (ir.pointerId != null && e.pointerId !== ir.pointerId) return;

      const pid = ir.pointerId;

      if (ir.mode === 'press') {
        activatePress(ir.hit);
        const swallowClick = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          window.removeEventListener('click', swallowClick, true);
        };
        window.addEventListener('click', swallowClick, true);
        window.setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);
        releaseCaptured(pid);
        interactionRef.current = { mode: 'idle' };
        wrap.style.cursor = hoverRef.current ? 'grab' : 'default';
        return;
      }
      if (ir.mode === 'pan') {
        releaseCaptured(pid);
        interactionRef.current = { mode: 'idle' };
        wrap.style.cursor = hoverRef.current ? 'grab' : 'default';
        return;
      }
      if (ir.mode === 'hub') {
        releaseCaptured(pid);
        interactionRef.current = { mode: 'idle' };
        wrap.style.cursor = hoverRef.current ? 'grab' : 'default';
        return;
      }
      if (ir.mode === 'drag') {
        if (ir.kind && ir.id) commitOrbitAfterDrag(ir.kind, ir.id);
        releaseCaptured(pid);
        interactionRef.current = { mode: 'idle' };
        wrap.style.cursor = hoverRef.current ? 'grab' : 'default';
        return;
      }
      if (ir.mode === 'grab' && ir.id) {
        onSelectNode(ir.id);
      }
      releaseCaptured(pid);
      interactionRef.current = { mode: 'idle' };
      wrap.style.cursor = hoverRef.current ? 'grab' : 'default';
    };

    const onPointerMove = (e) => {
      if (document.hidden || !document.hasFocus()) return;
      const target = e.target;
      const { mx, my } = localFromClient(e.clientX, e.clientY);
      mouseRef.current = { x: mx, y: my, in: true, overMap: target instanceof Node && wrap.contains(target) };

      const ir = interactionRef.current;
      if (ir.mode === 'press' || ir.mode === 'pan') {
        if (!e.isPrimary) return;
        if (ir.pointerId != null && e.pointerId !== ir.pointerId) return;
        const moved = Math.hypot(mx - (ir.startMx ?? 0), my - (ir.startMy ?? 0));
        const thresh = ir.hit ? 28 : (ir.dragThresh ?? 14);
        if (ir.mode === 'press' && moved > thresh) ir.mode = 'pan';
        if (ir.mode === 'pan') {
          const { w, h } = sizeRef.current;
          if (moved > Math.hypot(w, h) + 80) {
            interactionRef.current = { mode: 'idle' };
            return;
          }
          setPan((ir.originX ?? 0) + (mx - ir.startMx), (ir.originY ?? 0) + (my - ir.startMy));
          wrap.style.cursor = 'grabbing';
        }
        return;
      }
      if (ir.mode !== 'grab' && ir.mode !== 'drag') return;
      if (!e.isPrimary) return;
      if (ir.pointerId != null && e.pointerId !== ir.pointerId) return;

      const moved = Math.hypot(mx - (ir.startMx ?? 0), my - (ir.startMy ?? 0));
      const thresh = ir.dragThresh ?? 5;
      if (ir.mode === 'grab' && moved > thresh) ir.mode = 'drag';
      if (ir.mode === 'drag' && ir.kind && ir.id != null) {
        const list = ir.kind === 'main' ? runtimeRef.current : moonRuntimeRef.current;
        const target = list.find((n) => n.id === ir.id);
        if (target) {
          target.x = mx - (ir.offsetX ?? 0);
          target.y = my - (ir.offsetY ?? 0);
          target.vx = 0;
          target.vy = 0;
        }
        wrap.style.cursor = 'grabbing';
      }
    };

    const onPointerLeaveWindow = (e) => {
      if (e.relatedTarget) return;
      mouseRef.current.in = false;
      mouseRef.current.overMap = false;
    };

    const onPointerDown = (e) => {
      if (!e.isPrimary) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (backRef.current && backRef.current.contains(e.target)) return;
      if (e.pointerType === 'touch') {
        e.preventDefault();
      }

      const phone = sizeRef.current.w < 768;
      const slop = phone && selectionRef.current ? -10 : pointerHitSlop(e.pointerType);
      const { mx, my } = localFromClient(e.clientX, e.clientY);
      mouseRef.current = { x: mx, y: my, in: true, overMap: true };
      if (phone) {
        interactionRef.current = {
          mode: 'press',
          hit: identifyPress(mx, my, slop),
          startMx: mx,
          startMy: my,
          originX: viewPan.x,
          originY: viewPan.y,
          pointerId: e.pointerId,
          dragThresh: dragThresholdFor(e.pointerType),
        };
        try {
          wrap.setPointerCapture(e.pointerId);
        } catch (_) {
          /* noop */
        }
        return;
      }
      const world = worldFromScreen(mx, my);
      if (hubHit(world.x, world.y, slop)) {
        if (lineageOpenRef.current) onSelectNode(centerRef.current.id);
        else onToggleLineageRef.current?.();
        interactionRef.current = { mode: 'hub', pointerId: e.pointerId };
        try {
          wrap.setPointerCapture(e.pointerId);
        } catch (_) {
          /* noop */
        }
        return;
      }
      if (lineageProgressRef.current > 0.45 && lineageDisplayRef.current) {
        const ancestorId = lineageHitTest(mx, my, lineageDisplayRef.current, slop + 4);
        if (ancestorId && ancestorId !== 'I1') {
          onSelectNode(ancestorId);
          interactionRef.current = { mode: 'hub', pointerId: e.pointerId };
          try {
            wrap.setPointerCapture(e.pointerId);
          } catch (_) {
            /* noop */
          }
          return;
        }
        if (lineageProgressRef.current > 0.72) {
          interactionRef.current = { mode: 'idle' };
          return;
        }
      }
      const hit = pickMapTarget(mx, my, runtimeRef.current, moonRuntimeRef.current, slop);
      if (!hit) {
        interactionRef.current = { mode: 'idle' };
        return;
      }
      const list = hit.kind === 'main' ? runtimeRef.current : moonRuntimeRef.current;
      const node = list.find((n) => n.id === hit.id);
      if (!node) return;
      interactionRef.current = {
        mode: 'grab',
        kind: hit.kind,
        id: hit.id,
        offsetX: mx - node.x,
        offsetY: my - node.y,
        startMx: mx,
        startMy: my,
        pointerId: e.pointerId,
        dragThresh: dragThresholdFor(e.pointerType),
      };
      try {
        wrap.setPointerCapture(e.pointerId);
      } catch (_) {
        /* noop */
      }
    };

    const onPointerUp = (e) => {
      finishPointerSession(e);
    };

    const onPointerCancel = (e) => {
      finishPointerSession(e);
    };

    const releaseGesture = () => {
      const ir = interactionRef.current;
      if (ir.pointerId != null) {
        try {
          if (wrap.hasPointerCapture(ir.pointerId)) wrap.releasePointerCapture(ir.pointerId);
        } catch (_) {
          /* capture may already be released */
        }
      }
      interactionRef.current = { mode: 'idle' };
    };

    const onVisibility = () => {
      if (!document.hidden) return;
      releaseGesture();
    };
    const onBlur = () => releaseGesture();

    wrap.addEventListener('pointerdown', onPointerDown, { passive: false });
    wrap.addEventListener('pointerup', onPointerUp);
    wrap.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('pointermove', onPointerMove);
    document.documentElement.addEventListener('pointerout', onPointerLeaveWindow);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);

    const ctx = canvas.getContext('2d');
    let drawnMx = NaN;
    let drawnMy = NaN;
    let drawnHover;
    let drawnHub = false;
    let drawnSel;
    let drawnPlace = null;
    let drawnW = 0;
    let drawnH = 0;
    let drawnEpoch = -1;
    let drawnProgress = -1;
    let lineageSettled = false;
    let lineageIdeal = null;
    let ringCaches = [];
    let ringOffsets = null;

    const tick = (t) => {
      if (!runtimeRef.current.length && nodes.length) syncRuntimeSize();
      const { w, h, cx, cy, minDim } = sizeRef.current;
      if (w < 32 || h < 32) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const phone = w < 768;
      if (!phone) {
        viewPan.x = 0;
        viewPan.y = 0;
      } else if (viewPan.mode !== lineageOpenRef.current || viewPan.w !== w || viewPan.h !== h) {
        viewPan.x = 0;
        viewPan.y = 0;
        viewPan.mode = lineageOpenRef.current;
        viewPan.w = w;
        viewPan.h = h;
      }
      const parallaxX = !phone && mouseRef.current.in ? (mx - w / 2) * 0.028 : 0;
      const parallaxY = !phone && mouseRef.current.in ? (my - h / 2) * 0.028 : 0;
      const tcx = cx + parallaxX;
      const tcy = cy + parallaxY;

      const lineageTarget = lineageOpenRef.current ? 1 : 0;
      lineageProgressRef.current += (lineageTarget - lineageProgressRef.current) * 0.065;
      if (Math.abs(lineageTarget - lineageProgressRef.current) < 0.004) {
        lineageProgressRef.current = lineageTarget;
      }
      const lineageProgress = lineageProgressRef.current;
      if (!lineageLayoutRef.current || lineageLayoutRef.current.w !== w || lineageLayoutRef.current.h !== h) {
        lineageLayoutRef.current = layoutLineage(w, h);
      }
      const lineageLayout = lineageLayoutRef.current;
      const hubEase = lineageProgress * lineageProgress * (3 - 2 * lineageProgress);
      const lineageSelf = lineageLayout.positions.get('I1');
      const baseHubR = sizeRef.current.hubR || portraitRadius(minDim, w);
      const restX = lineageSelf?.x ?? cx;
      const restY = lineageSelf?.y ?? cy;
      const anchorX = cx + (restX - cx) * hubEase;
      const anchorY = cy + (restY - cy) * hubEase;
      const hx = anchorX + parallaxX;
      const hy = anchorY + parallaxY;
      const drawnHubR = baseHubR + (Math.min(lineageSelf?.r ?? baseHubR, baseHubR * 0.62) - baseHubR) * hubEase;
      hubDrawRef.current = { x: hx, y: hy, r: drawnHubR, progress: lineageProgress };
      const back = backRef.current;
      if (back && lineageSelf) {
        const x = Math.round(hx + viewPan.x);
        let y = Math.round(hy + lineageSelf.r + (phone ? 16 : 40) + viewPan.y);
        if (phone) {
          const footer = document.querySelector('.cosmos-footer');
          const footerTop = footer ? footer.getBoundingClientRect().top : h - 120;
          const limit = footerTop - (back.offsetHeight || 22) - 8;
          if (y > limit) y = limit;
        }
        const visible = lineageProgress > 0.82;
        if (back.dataset.x !== String(x) || back.dataset.y !== String(y)) {
          back.dataset.x = String(x);
          back.dataset.y = String(y);
          back.style.left = `${x}px`;
          back.style.top = `${y}px`;
        }
        back.style.opacity = visible ? '1' : '0';
        back.style.pointerEvents = visible ? 'auto' : 'none';
      }
      let lineageMoving = false;
      const fanHold = lineageProgress === 1
        && drawnProgress === 1
        && lineageSettled
        && lineageDisplayRef.current
        && drawnMx === mx
        && drawnMy === my
        && drawnW === w
        && drawnH === h;
      if (lineageProgress >= 1 && !fanHold) {
        const ideal = lineageDisplayPositions(lineageLayout, anchorX, anchorY, 1);
        const rings = stepRingMotion(parallaxX, parallaxY, lineageLayout.maxGen, lineageMotionRef.current);
        const shifted = shiftLineage(ideal, rings.offsets, lineageLayout.maxGen);
        ringOffsets = rings.offsets;
        lineageIdeal = shifted.ideal;
        lineageDisplayRef.current = shifted.display;
        lineageRingsRef.current = shifted.ringCenters;
        lineageMoving = rings.moving;
        lineageSettled = !rings.moving;
      } else if (lineageProgress > 0.01 && !fanHold) {
        const ideal = lineageDisplayPositions(lineageLayout, anchorX, anchorY, lineageProgress);
        const stepped = stepLineageMotion(ideal, parallaxX, parallaxY, lineageMotionRef.current);
        lineageDisplayRef.current = stepped.display;
        lineageRingsRef.current = stepped.ringCenters;
        lineageMoving = stepped.moving;
        lineageSettled = false;
        lineageIdeal = null;
        ringCaches = [];
        ringOffsets = null;
      } else if (lineageProgress <= 0.01) {
        lineageMotionRef.current.clear();
        lineageDisplayRef.current = null;
        lineageRingsRef.current = null;
        lineageSettled = false;
        lineageIdeal = null;
        ringCaches = [];
        ringOffsets = null;
      }

      const spring = 0.028;
      const damping = 0.94;
      const dt = 1;

      const ir = interactionRef.current;
      const pinMain =
        (ir.mode === 'grab' || ir.mode === 'drag') && ir.kind === 'main' && ir.id ? ir.id : null;
      const pinMoon =
        (ir.mode === 'grab' || ir.mode === 'drag') && ir.kind === 'moon' && ir.id ? ir.id : null;

      for (const n of runtimeRef.current) {
        if (n.id === pinMain) {
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        const xScale = sizeRef.current.xScale || 1;
        const yScale = sizeRef.current.yScale || 1;
        let tx = tcx + Math.cos(n.angle) * n.orbitR * xScale;
        let ty = tcy + Math.sin(n.angle) * n.orbitR * yScale;
        if (sizeRef.current.bounds) {
          const cleared = clearOfPortrait(tx, ty, tcx, tcy, baseHubR + n.bubbleRadius + 16);
          tx = cleared.x;
          ty = cleared.y;
        }
        const fx = (tx - n.x) * spring;
        const fy = (ty - n.y) * spring;
        n.vx = (n.vx + fx * dt) * damping;
        n.vy = (n.vy + fy * dt) * damping;
        n.x += n.vx * dt;
        n.y += n.vy * dt;
        if (n.id !== pinMain) clampNodeSpeed(n, minDim * 0.0021);
      }

      separateMainNodes(runtimeRef.current, minDim, pinMain);

      for (const n of runtimeRef.current) {
        if (n.id === pinMain) continue;
        n.vx *= 0.84;
        n.vy *= 0.84;
        clampNodeSpeed(n, minDim * 0.00185);
      }

      const idToMain = new Map(runtimeRef.current.map((n) => [n.id, n]));
      /** Moons: ease toward orbit target (no velocity integration) — avoids resonance with moving parents + separation. */
      const moonFollow = 0.09;
      for (const m of moonRuntimeRef.current) {
        if (m.id === pinMoon) {
          m.vx = 0;
          m.vy = 0;
          continue;
        }
        const p = idToMain.get(m.parentId);
        if (!p) continue;
        const tx = p.x + Math.cos(m.angle) * m.orbitR;
        const ty = p.y + Math.sin(m.angle) * m.orbitR;
        m.x += (tx - m.x) * moonFollow;
        m.y += (ty - m.y) * moonFollow;
        m.vx = 0;
        m.vy = 0;
      }

      nudgeMoonSiblingsApart(moonRuntimeRef.current, minDim, pinMoon);
      nudgeMoonsFromOtherMainBubbles(
        moonRuntimeRef.current,
        runtimeRef.current,
        minDim,
        pinMoon,
      );

      const idToNode = new Map(
        [...runtimeRef.current, ...moonRuntimeRef.current].map((n) => [n.id, n]),
      );

      let hoverId = null;
      let lineageHover = null;
      let hubHover = false;
      if (mouseRef.current.overMap && fanHold) {
        lineageHover = drawnHover || null;
        hubHover = drawnHub;
      } else if (mouseRef.current.overMap) {
        const hoverSlop =
          typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches ? 10 : 0;
        const wx = mx - viewPan.x;
        const wy = my - viewPan.y;
        if (hubHit(wx, wy, hoverSlop)) hubHover = true;
        if (lineageProgress > 0.4 && lineageDisplayRef.current) {
          const ancestorHit = lineageHitTest(wx, wy, lineageDisplayRef.current, hoverSlop + 8);
          lineageHover = ancestorHit && ancestorHit !== 'I1' ? ancestorHit : null;
        }
        if (!lineageHover && !hubHover && lineageProgress < 0.72) {
          const h = pickMapTarget(wx, wy, runtimeRef.current, moonRuntimeRef.current, hoverSlop);
          if (h) hoverId = h.id;
        }
      }
      lineageHoverRef.current = lineageHover;
      hoverRef.current = hoverId;
      const dragging = interactionRef.current.mode === 'drag';
      const mapHot = !!(hoverId || lineageHover || hubHover || dragging);
      if (!dragging) wrap.style.cursor = mapHot ? 'grab' : 'default';
      document.documentElement.dataset.mapHot = mapHot ? '1' : '0';

      const selNow = selectionRef.current;
      const placeNow = placeHighlightRef?.current ?? null;
      const pointerMoved = drawnMx !== mx || drawnMy !== my;
      if (
        lineageProgress === 1
        && !lineageMoving
        && lineageSettled
        && drawnMx === mx
        && drawnMy === my
        && drawnHover === lineageHover
        && drawnHub === hubHover
        && drawnSel === selNow
        && drawnPlace === placeNow
        && drawnW === w
        && drawnH === h
        && drawnEpoch === lineagePhotoEpoch()
        && drawnProgress === lineageProgress
        && interactionRef.current.mode === 'idle'
      ) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      drawnMx = mx;
      drawnMy = my;
      drawnHover = lineageHover;
      drawnHub = hubHover;
      drawnSel = selNow;
      drawnPlace = placeNow;
      drawnW = w;
      drawnH = h;
      drawnEpoch = lineagePhotoEpoch();
      drawnProgress = lineageProgress;

      const hoveredMoonParentId = hoverId
        ? moonRuntimeRef.current.find((m) => m.id === hoverId)?.parentId ?? null
        : null;

      ctx.clearRect(0, 0, w, h);
      if (viewPan.x || viewPan.y) ctx.translate(viewPan.x, viewPan.y);

      const hubR = drawnHubR;
      const pulse = 1 + 0.04 * Math.sin(t * 0.0022);
      const sel = selectionRef.current;
      const projectFade = 1 - lineageProgress;
      const roleFilter = lineageProgress < 0.45 ? placeNow : null;
      const roleAlpha = (match) => projectFade * (roleFilter ? (match ? 1 : 0.16) : 1);
      // Hub→node spokes lit for matching projects, or parents of matching moons (path out).
      // Related project↔project links stay dim — only the direct line from Kai.
      const roleSpokeParents = new Set();
      if (roleFilter) {
        for (const m of moonRuntimeRef.current) {
          if (m.mapRole === roleFilter) roleSpokeParents.add(m.parentId);
        }
      }

      if (projectFade > 0.012) {
      for (const link of linksRef.current) {
        const a = idToNode.get(link.fromId);
        const b = idToNode.get(link.toId);
        if (!a || !b) continue;
        const hot = !roleFilter && (sel === link.fromId || sel === link.toId);
        ctx.save();
        ctx.strokeStyle = hot ? 'rgba(130, 220, 160, 0.5)' : 'rgba(90, 170, 120, 0.22)';
        ctx.globalAlpha = roleFilter ? projectFade * 0.1 : projectFade;
        ctx.lineWidth = hot ? 1.75 : 1;
        ctx.setLineDash(hot ? [] : [4, 6]);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.restore();
      }

      const grad = ctx.createRadialGradient(tcx, tcy, hubR * 0.2, tcx, tcy, hubR * 2.2);
      grad.addColorStop(0, hubHover ? 'rgba(150, 240, 180, 0.22)' : 'rgba(120, 220, 160, 0.1)');
      grad.addColorStop(0.5, hubHover ? 'rgba(80, 160, 100, 0.1)' : 'rgba(60, 120, 80, 0.04)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.globalAlpha = projectFade;
        ctx.beginPath();
      ctx.arc(tcx, tcy, hubR * 2.2, 0, Math.PI * 2);
      ctx.fill();

      const haloScale = hubHover ? 1.06 : 1;
      ctx.strokeStyle = hubHover ? 'rgba(190, 245, 205, 0.78)' : 'rgba(140, 220, 170, 0.42)';
      ctx.globalAlpha = projectFade;
      ctx.lineWidth = hubHover ? 2 : 1.5;
      ctx.beginPath();
      ctx.arc(tcx, tcy, hubR * pulse * haloScale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = hubHover ? 'rgba(232, 210, 140, 0.55)' : 'rgba(200, 190, 120, 0.28)';
      ctx.globalAlpha = projectFade;
      ctx.beginPath();
      ctx.arc(tcx, tcy, hubR * pulse * 1.08 * haloScale + 6, 0, Math.PI * 2);
      ctx.stroke();

      for (const n of runtimeRef.current) {
        const hover = !roleFilter && (n.id === hoverRef.current || n.id === hoveredMoonParentId);
        const active = !roleFilter && n.id === sel;
        const spokeHot = !!(roleFilter && (n.mapRole === roleFilter || roleSpokeParents.has(n.id)));
        ctx.save();
        ctx.setLineDash([]);
        ctx.strokeStyle = active || hover || spokeHot
          ? 'rgba(155, 235, 185, 0.7)'
          : 'rgba(105, 175, 135, 0.26)';
        ctx.globalAlpha = roleFilter ? (spokeHot ? projectFade : projectFade * 0.12) : projectFade;
        ctx.lineWidth = active || spokeHot ? 1.85 : hover ? 1.35 : 1;
        ctx.beginPath();
        ctx.moveTo(tcx, tcy);
        ctx.lineTo(n.x, n.y);
        ctx.stroke();
        ctx.restore();
      }

      const moons = moonRuntimeRef.current;
      const sortedMoons = [...moons].sort((a, b) => a.y - b.y);
      for (const m of sortedMoons) {
        const p = idToMain.get(m.parentId);
        if (!p) continue;
        const mActive = !roleFilter && sel === m.id;
        const mHover = !roleFilter && hoverRef.current === m.id;
        const roleMatch = !roleFilter || m.mapRole === roleFilter;
        const spokeHot = !!(roleFilter && m.mapRole === roleFilter);
        const lineHot = mActive || mHover || spokeHot;
        const mAccent = mapAccentForRole(m.mapRole);
        const mr = m.radius * (mHover || spokeHot ? 1.08 : 1);
        const fade = roleAlpha(roleMatch);
        ctx.save();
        ctx.setLineDash([5, 4]);
        ctx.strokeStyle = lineHot ? 'rgba(165, 225, 195, 0.78)' : 'rgba(130, 195, 160, 0.42)';
        ctx.globalAlpha = roleFilter ? (spokeHot ? projectFade : projectFade * 0.1) : projectFade;
        ctx.lineWidth = lineHot ? 1.55 : 1.15;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(m.x, m.y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();

        const g = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, mr);
        ctx.globalAlpha = fade;
        g.addColorStop(0, `${mAccent}4d`);
        g.addColorStop(0.75, `${mAccent}26`);
        g.addColorStop(1, 'rgba(6, 14, 10, 0.94)');
        ctx.beginPath();
        ctx.arc(m.x, m.y, mr, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.strokeStyle = mActive || (roleFilter && roleMatch) ? mAccent : `${mAccent}88`;
        ctx.lineWidth = mActive || (roleFilter && roleMatch) ? 2.2 : 1;
        ctx.globalAlpha = (mActive || (roleFilter && roleMatch) ? 1 : 0.78) * fade;
        ctx.stroke();
        ctx.globalAlpha = fade;

        const mimg = m.icon ? imagesRef.current[m.icon] : null;
        if (mimg && mimg.complete && mimg.naturalWidth > 0) {
          drawImageContainedInCircle(ctx, mimg, m.x, m.y, mr * 0.72);
        }

        const lines = labelLines(m.label, 16, 3).map((line) => line.toUpperCase());
        const fs = Math.max(5, minDim * 0.0115);
        ctx.font = titleFont(fs, 500);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = 'rgba(220, 245, 228, 0.88)';
        const ly = m.y + mr + 4;
        ctx.letterSpacing = `${fs * TITLE_TRACKING_EM}px`;
        lines.forEach((line, i) => {
          ctx.fillText(line, m.x, ly + i * (fs + 1.5));
        });
        ctx.letterSpacing = '0px';
      }

      const sorted = [...runtimeRef.current].sort((a, b) => a.y - b.y);
      for (const n of sorted) {
        const roleMatch = !roleFilter || n.mapRole === roleFilter;
        const roleHot = !!(roleFilter && roleMatch);
        const r = n.bubbleRadius * (n.id === hoverRef.current || roleHot ? 1.08 : 1);
        const active = n.id === selectionRef.current;
        const accent = mapAccentForRole(n.mapRole);
        const fade = roleAlpha(roleMatch);

        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r);
        ctx.globalAlpha = fade;
        g.addColorStop(0, `${accent}55`);
        g.addColorStop(0.72, `${accent}28`);
        g.addColorStop(1, 'rgba(5, 14, 10, 0.9)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();

        ctx.strokeStyle = active || roleHot ? accent : `${accent}88`;
        ctx.lineWidth = active || roleHot ? 2.2 : 1;
        ctx.globalAlpha = (active || roleHot ? 1 : 0.78) * fade;
        ctx.stroke();
        ctx.globalAlpha = fade;

        const img = n.icon ? imagesRef.current[n.icon] : null;
        if (img && img.complete && img.naturalWidth > 0) {
          const clipR = r * 0.72;
          drawImageContainedInCircle(ctx, img, n.x, n.y, clipR);
        }

        const titleLines = labelLines(n.title, 20, 3).map((line) => line.toUpperCase());
        const tfs = Math.max(6, minDim * 0.0125);
        ctx.font = titleFont(tfs, 600);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = 'rgba(230, 250, 238, 0.92)';
        const ty = n.y + r + 6;
        ctx.letterSpacing = `${tfs * TITLE_TRACKING_EM}px`;
        titleLines.forEach((line, li) => {
          ctx.fillText(line, n.x, ty + li * (tfs + 2));
        });
        ctx.letterSpacing = '0px';
      }
      }

      const cc = centerRef.current;
      if (lineageProgress >= 1 && lineageIdeal && lineageDisplayRef.current && ringOffsets) {
        const cacheKey = `${canvas.width}x${canvas.height}x${Math.round(anchorX)}x${Math.round(anchorY)}`;
        const photoEpoch = lineagePhotoEpoch();
        const maxGen = lineageLayout.maxGen;
        for (let gen = 1; gen <= maxGen; gen += 1) {
          let layers = ringCaches[gen];
          const stale = !layers || layers.some((layer) => (
            !layer.canvas || layer.key !== cacheKey || (layer.epoch !== photoEpoch && !pointerMoved)
          ));
          if (!stale) continue;
          const slices = lineageRingSlices(lineageLayout, lineageIdeal, anchorX, anchorY, gen);
          if (!layers || layers.length !== slices.length) {
            layers = slices.map(() => ({ canvas: null, key: '', epoch: -1 }));
            ringCaches[gen] = layers;
          }
          for (let index = 0; index < slices.length; index += 1) {
            const slice = slices[index];
            const layer = layers[index];
            if (layer.canvas && layer.key === cacheKey && (layer.epoch === photoEpoch || pointerMoved)) continue;
            const bw = slice.w;
            const bh = slice.h;
            if (bw < 1 || bh < 1) return;
            if (!layer.canvas) layer.canvas = document.createElement('canvas');
            layer.canvas.width = Math.max(1, Math.ceil(bw * dpr));
            layer.canvas.height = Math.max(1, Math.ceil(bh * dpr));
            const cacheCtx = layer.canvas.getContext('2d');
            cacheCtx.setTransform(dpr, 0, 0, dpr, -slice.x * dpr, -slice.y * dpr);
            cacheCtx.clearRect(slice.x, slice.y, bw, bh);
            drawLineage(cacheCtx, lineageLayout, lineageIdeal, anchorX, anchorY, {
              hoverId: null,
              selectedId: null,
              ringCenters: null,
              onlyGen: gen,
              glow: false,
              spokes: false,
              arcs: false,
              people: !slice.title,
              continuations: !slice.title && gen === maxGen,
              title: !!slice.title,
              angle0: slice.a0,
              angle1: slice.a1,
            });
            layer.key = cacheKey;
            layer.epoch = photoEpoch;
            layer.x = slice.x;
            layer.y = slice.y;
            layer.w = bw;
            layer.h = bh;
          }
        }
        const hoverFocus = lineageHoverRef.current;
        const focusId = hoverFocus
          || (sel && sel !== 'I1' && lineageDisplayRef.current.has(sel) ? sel : null);
        const placeLit = placeNow && !hoverFocus ? birthplaceLit(placeNow) : null;
        drawLineageGlow(ctx, lineageLayout, hx, hy, 1, 1);
        drawLineageSpokes(ctx, lineageDisplayRef.current, focusId ? chainToRoot(focusId) : null);
        drawLineageArcs(ctx, lineageLayout, lineageDisplayRef.current, hx, hy, lineageRingsRef.current);
        for (let gen = maxGen; gen >= 1; gen -= 1) {
          const layers = ringCaches[gen];
          const off = ringOffsets[gen] || ringOffsets[0] || { x: 0, y: 0 };
          if (!layers) continue;
          layers.forEach((layer) => {
            if (!layer.canvas) return;
            ctx.drawImage(layer.canvas, layer.x + off.x, layer.y + off.y, layer.w, layer.h);
          });
        }
        if (placeLit) {
          ctx.save();
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.fillStyle = 'rgba(4, 10, 8, 0.58)';
          ctx.fillRect(0, 0, w, h);
          ctx.restore();
          drawLineageSpokes(ctx, lineageDisplayRef.current, placeLit, true);
          drawLineage(ctx, lineageLayout, lineageDisplayRef.current, hx, hy, {
            hoverId: null,
            selectedId: sel,
            ringCenters: lineageRingsRef.current,
            overlay: true,
            litIds: placeLit,
            spokes: false,
            glow: false,
          });
        } else if (focusId) {
          ctx.save();
          drawLineage(ctx, lineageLayout, lineageDisplayRef.current, hx, hy, {
            hoverId: lineageHoverRef.current,
            selectedId: sel,
            ringCenters: lineageRingsRef.current,
            overlay: true,
            spokes: false,
            glow: false,
          });
          ctx.restore();
        }
      } else if (lineageProgress > 0.01 && lineageDisplayRef.current) {
        const placeLit = placeNow ? birthplaceLit(placeNow) : null;
        ctx.save();
        drawLineage(ctx, lineageLayout, lineageDisplayRef.current, hx, hy, {
          hoverId: lineageHoverRef.current,
          selectedId: sel,
          ringCenters: lineageRingsRef.current,
          litIds: placeLit,
        });
        ctx.restore();
      }
      ctx.globalAlpha = 1;
      const cimg = cc.icon ? imagesRef.current[cc.icon] : null;
      const portraitR = hubR * (hubHover ? 1.08 : 1);
      const hubInner = Math.max(0, portraitR - 1);

      const cg = ctx.createRadialGradient(hx, hy, 0, hx, hy, hubInner);
      cg.addColorStop(0, 'rgba(35, 75, 52, 0.92)');
      cg.addColorStop(1, 'rgba(8, 18, 12, 0.96)');
      ctx.beginPath();
      ctx.arc(hx, hy, hubInner, 0, Math.PI * 2);
      ctx.fillStyle = cg;
      ctx.fill();

      if (cimg && cimg.complete && cimg.naturalWidth > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(hx, hy, hubInner, 0, Math.PI * 2);
        ctx.clip();
        ctx.globalAlpha = 0.72;
        drawImageCoverInCircle(ctx, cimg, hx, hy, hubInner);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgb(110, 195, 145)';
        ctx.beginPath();
        ctx.arc(hx, hy, hubInner, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'rgba(40, 110, 72, 0.28)';
        ctx.beginPath();
        ctx.arc(hx, hy, hubInner, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      const hubSelected = lineageProgress > 0.5;
      ctx.strokeStyle = hubHover
        ? 'rgba(214, 255, 226, 0.96)'
        : hubSelected
          ? 'rgba(244, 232, 196, 0.9)'
          : 'rgba(160, 230, 180, 0.48)';
      ctx.lineWidth = hubHover ? 2.6 : hubSelected ? 2.2 : 2;
      ctx.beginPath();
      ctx.arc(hx, hy, portraitR, 0, Math.PI * 2);
      ctx.stroke();

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      wrap.removeEventListener('pointerdown', onPointerDown);
      wrap.removeEventListener('pointerup', onPointerUp);
      wrap.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerout', onPointerLeaveWindow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      const ir = interactionRef.current;
      if (ir.pointerId != null) {
        try {
          if (wrap.hasPointerCapture(ir.pointerId)) wrap.releasePointerCapture(ir.pointerId);
        } catch (_) {
          /* noop */
        }
      }
    };
  }, [nodes, moonNodes, onSelectNode, selectionRef, placeHighlightRef, syncRuntimeSize]);

  return (
    <div
      className="power-map-canvas-wrap"
      ref={wrapRef}
      role="application"
      aria-label="Portfolio map. Click a project for details. Click the center portrait to open ancestors above it. With the tree open, click the portrait for Kai's bio. Use Back to return to the map."
    >
      <canvas ref={canvasRef} className="power-map-canvas" aria-hidden />
      {lineageOpen ? (
        <button
          type="button"
          className="lineage-back"
          ref={backRef}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onCloseLineageRef.current?.()}
        >
          Back
        </button>
      ) : null}
    </div>
  );
}

export default memo(PowerMapCanvas, (prev, next) => (
  prev.nodes === next.nodes
  && prev.moonNodes === next.moonNodes
  && prev.mainLinks === next.mainLinks
  && prev.onSelectNode === next.onSelectNode
  && prev.centerConfig === next.centerConfig
  && prev.selectionRef === next.selectionRef
  && prev.placeHighlightRef === next.placeHighlightRef
  && prev.lineageOpen === next.lineageOpen
  && prev.onToggleLineage === next.onToggleLineage
  && prev.onCloseLineage === next.onCloseLineage
));
